/**
 * The intro tour's behaviour, apart from how it is drawn.
 *
 * Owner's rules (2026-10-08):
 *   - an icon in the rail starts it;
 *   - it also starts by itself once, on a person's first sign-in to a console
 *     (remembered on the server, so a second device does not start it again);
 *   - every step has Skip and Exit, and both record it as seen;
 *   - when a console raises its tour version (a new module added) the icon is
 *     highlighted until the person opens the tour — it never pops up again.
 *
 * No React and no DOM, so the rules are tested without mounting anything
 * (test/tour.test.ts). `Tour.tsx` draws it.
 */

/** What the server remembers. `seenVersion` null: never shown. */
export interface TourRecord {
  readonly seenVersion: number | null;
  /**
   * Somebody else is at the keyboard — a support view or a platform login-as.
   * The tour is theirs to open, but it must not start by itself or be recorded
   * on the user's behalf, or the user's own first sign-in finds it "seen".
   */
  readonly onBehalf: boolean;
}

export interface TourStore {
  load(): Promise<TourRecord>;
  save(version: number): Promise<unknown>;
}

/** A step, as far as the rules care: which tour version added it. */
export interface TourStepVersion {
  /** The tour version this step arrived in. Absent: the first version. */
  readonly since?: number;
}

export interface TourState {
  /** `unavailable`: the marker could not be read. The icon still works. */
  readonly status: 'loading' | 'ready' | 'unavailable';
  readonly open: boolean;
  readonly step: number;
  /** A newer tour than the one this person last saw. */
  readonly highlight: boolean;
  /** The last attempt to record "seen" failed. Shown, never blocking. */
  readonly saveFailed: boolean;
  /**
   * While open from a highlighted icon: the version last seen, so a step
   * added after it can be marked new. Null otherwise.
   */
  readonly newAfter: number | null;
}

export interface TourOptions {
  /** This console's tour version. Raise it when a step is added. */
  readonly version: number;
  readonly store: TourStore;
  /** The steps this person will be shown — only what their role can open. */
  readonly steps: () => readonly TourStepVersion[];
  /** Called once per failed save, so the console can say so. */
  readonly onSaveFailed?: () => void;
}

/**
 * Where the tour opens for somebody who last saw `seen`: on the first step a
 * later version added, so "what's new" is what they read first. Back still
 * reaches the rest. From the start when there is no such step.
 */
export function firstNewStep(steps: readonly TourStepVersion[], seen: number | null): number {
  if (seen === null) return 0;
  const at = steps.findIndex((s) => (s.since ?? 1) > seen);
  return at === -1 ? 0 : at;
}

export class TourController {
  #state: TourState = {
    status: 'loading', open: false, step: 0, highlight: false, saveFailed: false, newAfter: null,
  };
  #seen: number | null = null;
  #onBehalf = false;
  /** Recorded during the current run, so closing does not save twice. */
  #savedThisRun = false;
  readonly #listeners = new Set<() => void>();
  readonly #opts: TourOptions;

  constructor(opts: TourOptions) {
    this.#opts = opts;
  }

  getState = (): TourState => this.#state;

  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => { this.#listeners.delete(listener); };
  };

  #set(next: Partial<TourState>): void {
    this.#state = { ...this.#state, ...next };
    for (const l of this.#listeners) l();
  }

  #stepCount(): number {
    return this.#opts.steps().length;
  }

  /** Read the marker; start the tour if this is the first sign-in. */
  async init(): Promise<void> {
    let record: TourRecord;
    try {
      record = await this.#opts.store.load();
    } catch {
      // Not knowing is not "never shown": starting a tour on every sign-in
      // because a read failed is the one outcome the owner ruled out.
      this.#set({ status: 'unavailable' });
      return;
    }
    this.#seen = record.seenVersion;
    this.#onBehalf = record.onBehalf;
    if (this.#state.open) {
      // Opened from the icon while the read was in flight.
      this.#set({ status: 'ready', highlight: false });
      return;
    }
    if (record.seenVersion === null && !record.onBehalf && this.#stepCount() > 0) {
      this.#set({ status: 'ready' });
      this.#begin(0, null);
      return;
    }
    this.#set({
      status: 'ready',
      highlight: !record.onBehalf && record.seenVersion !== null && record.seenVersion < this.#opts.version,
    });
  }

  /** The rail icon. */
  start(): void {
    if (this.#stepCount() === 0 || this.#state.open) return;
    const newAfter = this.#state.highlight ? this.#seen : null;
    this.#begin(firstNewStep(this.#opts.steps(), newAfter), newAfter);
  }

  #begin(step: number, newAfter: number | null): void {
    this.#savedThisRun = false;
    this.#set({ open: true, step, highlight: false, saveFailed: false, newAfter });
    void this.#record();
  }

  next(): void {
    if (this.#state.step >= this.#stepCount() - 1) {
      this.finish();
      return;
    }
    this.#set({ step: this.#state.step + 1 });
  }

  back(): void {
    if (this.#state.step > 0) this.#set({ step: this.#state.step - 1 });
  }

  /** Skip the rest of the tour. Recorded as seen. */
  skip(): void { this.#close(); }

  /** Leave the tour now (the close button, Escape). Recorded as seen. */
  exit(): void { this.#close(); }

  /** The last step's Done. */
  finish(): void { this.#close(); }

  #close(): void {
    if (!this.#state.open) return;
    this.#set({ open: false, newAfter: null });
    if (!this.#savedThisRun) void this.#record();
  }

  async #record(): Promise<void> {
    if (this.#onBehalf) return;
    const version = this.#opts.version;
    try {
      await this.#opts.store.save(version);
      this.#savedThisRun = true;
      this.#seen = Math.max(this.#seen ?? 0, version);
      if (this.#state.saveFailed) this.#set({ saveFailed: false });
    } catch {
      // Shown and not blocking: the tour keeps working, and the worst case is
      // that it starts again next time.
      if (!this.#state.saveFailed) {
        this.#set({ saveFailed: true });
        this.#opts.onSaveFailed?.();
      }
    }
  }
}
